import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, InputGroup, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Expected structure for emisores: [{ id, vRazonSocial, vRFC, series: [{ serie, nextFolio }] }]
// Orden contains datos fiscales: vRfc, vRazonSocial, vCodigoUso, vDescripcionUsoCFDI, vCodigoRegimen, vDescripcionRegimenFiscal, vCp, vNumRegTrib, vPaisFacturacion

const FacturarModal = ({ show, onHide, pago, onSubmit, orden }) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const navigate = useNavigate();
    const [emisores, setEmisores] = useState([]);
    const [formasPagoOptions, setFormasPagoOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const [emisorId, setEmisorId] = useState('');
    // restore serie/folio state to original names
    const [serie, setSerie] = useState('');
    const [folio, setFolio] = useState('');
    const [formaPago, setFormaPago] = useState('');
    const [fechaEmision, setFechaEmision] = useState('');

    // Productos editable (fields aligned with backend)
    const [productos, setProductos] = useState([]);

    // New: moneda, tipoCambio, metodoPagoIndicativo
    const [moneda, setMoneda] = useState('MXN');
    const [tipoCambio, setTipoCambio] = useState(1);
    const [monedas, setMonedas] = useState([]);
    const [metodoPagoIndicativo] = useState('PUE'); // solo indicativo, readonly

    // Predefined series list (static, no useState)
    const predefinedSeries = ['BB', 'STR', 'PPL', 'WU', 'EFE', 'CRE', 'A', 'P', 'E'];

    // compute min/max date strings for date input: max = today, min = now - 72 hours
    const { minDate, maxDate } = useMemo(() => {
        const now = new Date();
        const minDateObj = new Date(now.getTime() - 72 * 60 * 60 * 1000);
        return { minDate: minDateObj, maxDate: now };
    }, []);

    useEffect(() => {
        if (show) {
            // reset form
            setEmisorId('');
            setSerie('');
            setFolio('');
            // initialize formaPago from pago if available
            setFormaPago(pago && pago.vClave ? String(pago.vClave) : '');
            // set fecha emision to 5 minutes ago (matching FormularioFactura)
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
            setFechaEmision(fiveMinutesAgo);

            // Initialize moneda/tipoCambio from the pago if available
            const pagoMoneda = pago && (pago.vMoneda || pago.vMonedaPago) ? (pago.vMoneda || pago.vMonedaPago) : 'MXN';
            const pagoTipoCambio = pago && (pago.dTipoCambio || pago.dTipoCambio === 0) ? Number(pago.dTipoCambio) : (pago && (pago.tipoCambio || pago.dTipoCambioPago) ? Number(pago.tipoCambio || pago.dTipoCambioPago) : 1);
            setMoneda(pagoMoneda);
            setTipoCambio(isNaN(pagoTipoCambio) ? 1 : pagoTipoCambio);

            // initialize productos from pago (importe), codigo barras must be 'SERVICIO'
            // dImportePago (pago) may be the total (including IVA) - we must split it into base and IVA
            const totalPago = (pago && (pago.dImportePago)) ? Number(pago.dImportePago) : 0;
            const taxRate = 0.16; // default tax rate
            let baseImporte = 0;
            let iva = 0;
            if (totalPago > 0) {
                // use 6 decimals precision
                baseImporte = +(totalPago / (1 + taxRate)).toFixed(6);
                iva = +(totalPago - baseImporte).toFixed(6);
            }
            const defaultTotal = +(baseImporte + iva).toFixed(6);

            setProductos([{
                vCodigoProductoServicioSat: '43232611',
                vNombreProducto: 'Software de puntos de venta pos (Programa punto de venta POS)',
                dCantidad: 1,
                vClaveUnidadSat: 'E48',
                dPrecioUnitario: parseFloat(baseImporte.toFixed(6)), // unit price without IVA (6 decimals)
                dImporte: parseFloat(baseImporte.toFixed(6)), // importe (base, 6 decimals)
                dDescuento: 0,
                vCodigoBarras: 'SERVICIO',
                // tax fields
                dTasaCuotaIVA: taxRate,
                vTipoFactorIVA: 'Tasa',
                dIVA: parseFloat(iva.toFixed(6)),
                dTotal: parseFloat(defaultTotal.toFixed(6))
            }]);

            // load emisores and formas de pago
            loadCatalogs();
        }
    }, [show, pago]);

    const handleFechaChange = (selectedDate) => {
        if (!selectedDate) {
            setFechaEmision(null);
            return;
        }

        // Convierte el valor a un objeto Date si no lo es
        const dateObj = typeof selectedDate === "string"
            ? new Date(selectedDate)
            : selectedDate;

        setFechaEmision(dateObj);
    };

    const fetchSiguienteFolio = async (serieToQuery, vRFC) => {
        if (!serieToQuery || !vRFC) return null;
        try {
            const resp = await fetch(`/api/Facturas/ObtenerSiguienteFolio?serie=${encodeURIComponent(serieToQuery)}&vRFC=${encodeURIComponent(vRFC)}`, {
                headers: { Authorization: token ? 'Bearer ' + token : '', 'Content-Type': 'application/json' }
            });
            if (resp.ok) {
                const data = await resp.json();
                if (data && (data.siguienteFolio || data.siguienteFolio === 0)) return data.siguienteFolio;
            }
        } catch (err) {
            // ignore
        }
        return null;
    };

    const selectSerieBasedOnFormaPago = async (forma) => {
        if (!orden) return;
        let serieSeleccionada = '';
        const vRFC = orden.vRfc || '';
        if (vRFC === 'CACF9111245G4') {
            serieSeleccionada = 'A';
        } else {
            // We're creating an Ingreso
            if (forma === '01') serieSeleccionada = 'EFE';
            else if (forma === '03') serieSeleccionada = 'BB';
            else if (['04', '28'].includes(forma)) serieSeleccionada = 'STR';
            else if (forma === '99') serieSeleccionada = 'CRE';
            else serieSeleccionada = 'PPL';
        }

        if (serieSeleccionada) {
            setSerie(serieSeleccionada);
            // try to get siguiente folio
            const siguiente = await fetchSiguienteFolio(serieSeleccionada, vRFC);
            if (siguiente !== null) {
                setFolio(String(siguiente));
            } else {
                // fallback format
                if (vRFC) setFolio(`${vRFC}-${serieSeleccionada}-1`);
                else setFolio('');
            }
        }
    };

    const loadCatalogs = async () => {
        setLoading(true);
        try {
            // Emisores: POST to exact endpoint '/api/Facturas/ObtenerEmisores'
            try {
                const resE = await fetch('/api/Facturas/ObtenerEmisores', {
                    method: 'POST',
                    headers: {
                        Authorization: token ? 'Bearer ' + token : '',
                        'Content-Type': 'application/json'
                    }
                });
                const dataE = await resE.json().catch(() => null);
                if (resE.ok && dataE && dataE.iResponseType === 1) {
                    // dataE.detalles is a JSON string like '{"table2":[...]}'
                    let parsed = null;
                    try {
                        parsed = typeof dataE.detalles === 'string' ? JSON.parse(dataE.detalles) : dataE.detalles;
                    } catch (err) {
                        parsed = null;
                    }

                    const table2 = parsed && Array.isArray(parsed.table2) ? parsed.table2 : [];
                    const normalized = table2.map(d => ({
                        id: d.idDatoFiscal,
                        vRazonSocial: d.vRazonSocial,
                        vRFC: d.vRFC,
                        series: d.series || []
                    }));

                    setEmisores(normalized);

                    if (normalized.length > 0) setEmisorId(String(normalized[0].id));
                } else {
                    setEmisores([]);
                }
            } catch (err) {
                setEmisores([]);
            }

            // Formas de pago: POST exact endpoint '/api/Facturas/obtenerFormasdePago'
            try {
                const resF = await fetch('/api/Facturas/obtenerFormasdePago', {
                    method: 'POST',
                    headers: { Authorization: token ? 'Bearer ' + token : '' }
                });
                const dataF = await resF.json().catch(() => null);
                if (resF.ok && dataF && dataF.iResponseType === 1) {
                    let parsed = null;
                    try {
                        parsed = typeof dataF.detalles === 'string' ? JSON.parse(dataF.detalles) : dataF.detalles;
                    } catch (err) {
                        parsed = null;
                    }

                    const table2 = parsed && Array.isArray(parsed.table2) ? parsed.table2 : [];
                    const opciones = table2.map(d => ({ value: String(d.vClave), label: d.vDescripcionFormaPago }));

                    setFormasPagoOptions(opciones);

                    // Prefer the pago's forma de pago if provided and matches an option; otherwise use first option if none selected
                    const pagoClave = pago && pago.vClave ? String(pago.vClave) : '';
                    const match = pagoClave ? opciones.find(o => o.value === pagoClave) : null;
                    if (match) {
                        setFormaPago(pagoClave);
                        // select serie based on this forma
                        await selectSerieBasedOnFormaPago(pagoClave);
                    } else if (!formaPago && opciones.length > 0) {
                        setFormaPago(String(opciones[0].value));
                        // also select serie for default
                        await selectSerieBasedOnFormaPago(String(opciones[0].value));
                    }
                } else {
                    setFormasPagoOptions([]);
                }
            } catch (err) {
                setFormasPagoOptions([]);
            }
            // Monedas: POST to '/api/Facturas/ObtenerCatMonedas'
            try {
                const resM = await fetch('/api/Facturas/ObtenerCatMonedas', {
                    method: 'POST',
                    headers: { Authorization: token ? 'Bearer ' + token : '' }
                });
                const dataM = await resM.json().catch(() => null);
                if (resM.ok && dataM && dataM.iResponseType === 1) {
                    let parsedM = null;
                    try {
                        parsedM = typeof dataM.detalles === 'string' ? JSON.parse(dataM.detalles) : dataM.detalles;
                    } catch (err) {
                        parsedM = null;
                    }

                    const table2m = parsedM && Array.isArray(parsedM.table2) ? parsedM.table2 : [];
                    setMonedas(table2m);

                    // Prefer pago moneda if provided and exists in catalog
                    const pagoMon = pago && (pago.vMoneda || pago.vMonedaPago) ? (pago.vMoneda || pago.vMonedaPago) : null;
                    if (pagoMon) {
                        const match = table2m.find(m => String(m.vClaveMoneda || m.vMoneda || m.vCodigo) === String(pagoMon));
                        if (match) setMoneda(String(pagoMon));
                    } else if ((!moneda || moneda === '') && table2m.length > 0) {
                        setMoneda(String(table2m[0].vClaveMoneda || table2m[0].vMoneda || table2m[0].vCodigo || 'MXN'));
                    }
                }
            } catch (err) {
                // ignore, keep defaults
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!serie) {
            setFolio('');
            return;
        }

        const selectedEmisor = emisores.find(e => String(e.id) === String(emisorId));
        const vRFC = (selectedEmisor && selectedEmisor.vRFC) || '';

        (async () => {
            // try to get siguiente folio from API
            const siguiente = await fetchSiguienteFolio(serie, vRFC);
            if (siguiente !== null) {
                setFolio(String(siguiente));
                return;
            }

            // fallback to series object nextFolio
            if (selectedEmisor) {
                const seriesObj = (selectedEmisor.series || []).find(s => String(s.serie) === String(serie));
                if (seriesObj && seriesObj.nextFolio) {
                    setFolio(String(seriesObj.nextFolio));
                    return;
                }
            }

            // final fallback to RFC-series-1
            if (vRFC) {
                setFolio(`${vRFC}-${serie}-1`);
                return;
            }

            setFolio('');
        })();

    }, [serie, emisorId, emisores, orden]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const selectedEmisorId = emisorId || (emisores[0] && String(emisores[0].id)) || null;
        if (!selectedEmisorId) {
            if (onSubmit) onSubmit({ success: false, message: 'No Emisor seleccionado' });
            return;
        }

        // determine RFC reference
        const vRFCorden = (orden && (orden.vRfc)) || '';

        // compute folio if empty
        let finalFolio = folio;
        if (!finalFolio && serie) {
            const siguiente = await fetchSiguienteFolio(serie, vRFCorden);
            if (siguiente !== null) finalFolio = String(siguiente);
            else if (vRFCorden) finalFolio = `${vRFCorden}-${serie}-1`;
            else finalFolio = '1';
            setFolio(finalFolio);
        }

        // metodo de pago indicativo (PUE/PPD)
        const metodoPago = (formaPago === '99') ? 'PPD' : 'PUE';

        // Top-level receptor / fiscal fields (use values from orden when available)
        const vRFC = orden ? (orden.vRfc || '') : '';
        const vRazonSocial = orden ? (orden.vRazonSocial || '') : '';
        const vCodigoUso = orden ? (orden.vCodigoUso || 'S01') : 'S01';
        const vCodigoRegimen = orden ? (orden.vCodigoRegimen || '') : '';
        const vCP = orden ? orden.vCp : '00000';
        const vNumRT = orden ? orden.vNumRegTrib : '';
        const vPaisFacturacion = (orden && (orden.vPaisFacturacion !== undefined)) ? (orden.vPaisFacturacion) : null;
        const vEmail = orden ? (orden.vEmailFacturacion || '') : '';

        // Build dFechaEmision with time
        let dFechaEmision;
        if (fechaEmision) {
            // Formatea la fecha en el formato correcto: yyyy-MM-dd HH:mm:ss
            const year = fechaEmision.getFullYear();
            const month = String(fechaEmision.getMonth() + 1).padStart(2, "0");
            const day = String(fechaEmision.getDate()).padStart(2, "0");
            const hours = String(fechaEmision.getHours()).padStart(2, "0");
            const minutes = String(fechaEmision.getMinutes()).padStart(2, "0");
            const seconds = String(fechaEmision.getSeconds()).padStart(2, "0");
            dFechaEmision = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } else {
            const d = new Date();
            dFechaEmision = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
        }

        // Map products to exactly the fields requested
        const productosAgregadosPayload = (productos && productos.length > 0) ? productos.map(p => {
            const cantidad = p.dCantidad ?? 1;
            const precio = Number(p.dPrecioUnitario ?? 0);
            const importeVal = parseFloat((Number(cantidad) * Number(precio)).toFixed(6));
            const tasa = Number(p.dTasaCuotaIVA ?? 0);
            const ivaVal = parseFloat((importeVal * tasa).toFixed(6));
            const dTotalVal = parseFloat((importeVal + ivaVal).toFixed(6));

            return {
                vCodigoBarras: p.vCodigoBarras ?? 'SERVICIO',
                vNombreProducto: p.vNombreProducto ?? p.vDescripcion ?? '',
                dPrecioUnitario: parseFloat(precio.toFixed(6)),
                dTasaCuotaIVA: tasa,
                vTipoFactorIVA: p.vTipoFactorIVA ?? 'Tasa',
                dIVA: ivaVal,
                dImporte: importeVal,
                vClaveUnidadSat: p.vClaveUnidadSat ?? p.vClaveUnidad ?? '',
                vCodigoProductoServicioSat: p.vCodigoProductoServicioSat ?? p.vCodigoProductoServicioSat ?? p.vClaveProdServ ?? '',
                dTotal: dTotalVal,
                dCantidad: cantidad
            };
        }) : [];

        // Build final payload exactly with the keys requested
        const payload = {
            vCodigoUso: vCodigoUso,
            vClavePago: formaPago || '',
            vCodigoRegimen: vCodigoRegimen,
            vMoneda: moneda,
            vNumRT: vNumRT,
            vPaisFacturacion: vPaisFacturacion,
            vTipoDocumento: 'Ingreso',
            vSerie: serie || '',
            vRazonSocial: vRazonSocial,
            vRFC: vRFC,
            vCP: vCP,
            vEmail: vEmail,
            vFolio: finalFolio || '',
            idMetodoPago: metodoPago,
            vCondiciones: '',
            dFechaEmision: dFechaEmision,
            dTipoCambio: Number(tipoCambio) || 1,
            idEmisor: String(selectedEmisorId),
            productosAgregados: productosAgregadosPayload,
            idOrden: orden && (orden.idOrden) ? (orden.idOrden) : 0,
            idPago: pago && pago.idPago ? pago.idPago : null,
            // Enviar explícitamente el flag; si hay idPago habilitar (1), en otro caso enviar 0
            bActivarComprobacionUnicoPago: 0
        };

        // Sanitize payload: ensure description fields are NOT sent to endpoint
        const sanitizedPayload = JSON.parse(JSON.stringify(payload));
        if (sanitizedPayload.vDescripcionRegimenFiscal) delete sanitizedPayload.vDescripcionRegimenFiscal;
        if (sanitizedPayload.vDescripcionUsoCFDI) delete sanitizedPayload.vDescripcionUsoCFDI;
        // Also defensively remove from any nested receptor or similar fields if present
        if (sanitizedPayload.receptor) {
            if (sanitizedPayload.receptor.vDescripcionRegimenFiscal) delete sanitizedPayload.receptor.vDescripcionRegimenFiscal;
            if (sanitizedPayload.receptor.vDescripcionUsoCFDI) delete sanitizedPayload.receptor.vDescripcionUsoCFDI;
        }

        setLoading(true);
        try {
            const resp = await fetch('/api/Facturas/registrarFactura', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token ? 'Bearer ' + token : ''
                },
                body: JSON.stringify(sanitizedPayload)
            });

            const result = await resp.json().catch(() => null);
            if (resp.ok && result && result.iResponseType === 1) {
                // show success toast if available
                if (typeof showSuccessToast === 'function') showSuccessToast(result && (result.vMessage) ? (result.vMessage) : 'Factura registrada correctamente');

                // Redirect similar to FormularioFactura: use redirectUrl if provided, otherwise use idFactura
                try {
                    if (result && result.redirectUrl) {
                        navigate(result.redirectUrl);
                    } else if (result && result.idFactura) {
                        navigate(`/contabilidad/facturacion/facturas-emitidas/visualizacion/${result.idFactura}`);
                    }
                } catch (navErr) {
                    // ignore navigation errors
                    console.warn('Navigation error after factura:', navErr);
                }

                if (onSubmit) onSubmit({ success: true, data: result });
                onHide && onHide();
            } else {
                // prefer vMessage from API, otherwise generic
                const errMsg = (result && (result.vMessage)) || 'Error al registrar la factura';
                if (typeof showErrorToast === 'function') showErrorToast(errMsg);
                if (onSubmit) onSubmit({ success: false, error: result || { status: resp.statusText } });
            }
        } catch (err) {
            if (typeof showErrorToast === 'function') showErrorToast(err.message || 'Error de conexión');
            if (onSubmit) onSubmit({ success: false, error: err.message });
        } finally {
            setLoading(false);
        }
    };

    const isForeign = (orden && (orden.vRfc === 'XEXX010101000'));

    // Handlers for editable productos
    const handleProductChange = (index, field, value) => {
        // prevent changing codigo barras (must remain 'SERVICIO')
        if (field === 'vCodigoBarras') return;

        setProductos(prev => {
            const copy = [...prev];
            // normalize numeric fields
            if (field === 'dPrecioUnitario') value = Number(value || 0);
            if (field === 'dCantidad') value = Number(value || 0);
            if (field === 'dTasaCuotaIVA') value = Number(value || 0);

            copy[index] = { ...copy[index], [field]: value };
            // auto-update dImporte if price or cantidad changed
            const cantidad = Number(copy[index].dCantidad || 1);
            const precio = Number(copy[index].dPrecioUnitario || 0);
            const importe = parseFloat((cantidad * precio).toFixed(6));
            // store rounded values
            copy[index].dPrecioUnitario = parseFloat(precio.toFixed(6));
            copy[index].dImporte = importe;
            // recalculate IVA and total when tasa present
            const tasa = Number(copy[index].dTasaCuotaIVA ?? 0);
            const iva = parseFloat((importe * tasa).toFixed(6));
            copy[index].dIVA = iva;
            copy[index].dTotal = parseFloat((importe + iva).toFixed(6));
            // always ensure vCodigoBarras = 'SERVICIO'
            copy[index].vCodigoBarras = 'SERVICIO';
            return copy;
        });
    };
    const addProduct = () => {
        const defaultTasa = 0.16;
        setProductos(prev => ([...prev, { vCodigoProductoServicioSat: '', vNombreProducto: '', dCantidad: 1, vClaveUnidadSat: 'E48', dPrecioUnitario: parseFloat((0).toFixed(6)), dImporte: parseFloat((0).toFixed(6)), dDescuento: 0, vCodigoBarras: 'SERVICIO', dTasaCuotaIVA: defaultTasa, vTipoFactorIVA: 'Tasa', dIVA: parseFloat((0).toFixed(6)), dTotal: parseFloat((0).toFixed(6)) }]));
    };
    const removeProduct = (index) => {
        setProductos(prev => prev.filter((_, i) => i !== index));
    };

    // Preview object computed from current form state - only indicative
    const previewData = useMemo(() => {
        const selectedEmisorId = emisorId || (emisores[0] && String(emisores[0].id)) || null;
        const selectedEmisor = emisores.find(e => String(e.id) === String(selectedEmisorId));
        const vRFC = (selectedEmisor && selectedEmisor.vRFC) || '';

        const metodoPago = (formaPago === '99') ? 'PPD' : 'PUE';

        const receptor = orden ? {
            vRFC: orden.vRfc || '',
            vRazonSocial: orden.vRazonSocial || '',
            vCodigoUso: orden.vCodigoUso || 'S01',
            vCodigoRegimen: orden.vCodigoRegimen || '',
            vCp: orden.vCp || '00000',
            vNumRegTrib: orden.vNumRegTrib || '',
            vPaisFacturacion: orden.vPaisFacturacion || ''
        } : { vRFC: '', vRazonSocial: '', vCodigoUso: 'S01', vCp: '00000' };

        const cp = receptor.vCp && receptor.vCp.toString().padStart(5, '0').slice(0, 5);

        const now = fechaEmision ? fechaEmision : new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        return {
            idEmisor: selectedEmisorId ? Number(selectedEmisorId) : null,
            vSerie: serie || predefinedSeries[0],
            vFolio: folio || (vRFC ? `${vRFC}-${(serie || predefinedSeries[0])}-1` : ''),
            vClavePago: formaPago || (formasPagoOptions[0] && formasPagoOptions[0].value) || '',
            idMetodoPago: metodoPago,
            vMoneda: moneda,
            dTipoCambio: Number(tipoCambio) || 1,
            vTipoDocumento: 'Ingreso',
            vCP: cp,
            dFechaEmision: formattedDate,
            productosAgregados: productos,
            receptor,
            idOrden: orden && orden.idOrden ? orden.idOrden : 0,
            pagoId: pago ? (pago.idPago || null) : null,
            note: 'Estos datos son solo indicativos y pueden cambiar al enviar la solicitud.'
        };
    }, [emisorId, emisores, serie, folio, formaPago, fechaEmision, formasPagoOptions, orden, pago, productos, predefinedSeries, moneda, tipoCambio]);

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '0.95rem' }}>Facturar el pago como un Ingreso</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ fontSize: '0.85rem', maxHeight: '70vh', overflowY: 'auto' }}>
                    {loading && (
                        <div className="text-center py-3">
                            <Spinner animation="border" />
                        </div>
                    )}

                    {!loading && (
                        <>
                            <div className="alert alert-warning" role="alert">
                                <strong>Advertencia:</strong> Esta factura se registrará como <em>Ingreso</em> y el pago quedará marcado como <strong>facturado</strong>.
                            </div>
                            <Row className="g-3">
                                {/* Left: form fields */}
                                <Col md={6} xs={12}>
                                    <Row className="g-2">
                                        <Col xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Emisor</Form.Label>
                                                <Form.Select size="sm" value={emisorId} onChange={(e) => setEmisorId(e.target.value)}>
                                                    <option value="">Seleccione emisor</option>
                                                    {emisores.map((em) => (
                                                        <option key={em.id} value={em.id}>{em.vRazonSocial} - {em.vRFC}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        {/* RECEPTOR */}
                                        <Col xs={12}>
                                            <div className="mb-1"><strong>Receptor</strong></div>
                                            <div className="border rounded p-2" style={{ fontSize: '0.82rem' }}>

                                                <div className="mb-1"><strong>RFC:</strong> {orden ? orden.vRfc : '-'}</div>
                                                <div className="mb-1"><strong>Razón social:</strong> {orden ? orden.vRazonSocial : '-'}</div>
                                                <div className="mb-1"><strong>Uso CFDI:</strong> {orden ? ((orden.vCodigoUso) + ' ' + (orden.vDescripcionUsoCFDI)) : '-'}</div>
                                                <div className="mb-1"><strong>Régimen Fiscal:</strong> {orden ? orden.vCodigoRegimen + ' ' + orden.vDescripcionRegimenFiscal : '-'}</div>
                                                <div className="mb-1"><strong>Código Postal:</strong> {orden ? orden.vCp : '-'}</div>
                                                {isForeign && (
                                                    <>
                                                        <div className="mb-1"><strong>Num. Reg. Trib.:</strong> {orden.vNumRegTrib || '-'}</div>
                                                        <div className="mb-1"><strong>País facturación:</strong> {orden.vPaisFacturacion || '-'}</div>
                                                    </>
                                                )}
                                            </div>
                                        </Col>

                                        <Col md={6} xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Serie</Form.Label>
                                                <Form.Select size="sm" value={serie} onChange={(e) => setSerie(e.target.value)}>
                                                    <option value="">Seleccione serie</option>
                                                    {/* list series from selected emisor first */}
                                                    {(() => {
                                                        const selectedEmisor = emisores.find(e => String(e.id) === String(emisorId));
                                                        const series = selectedEmisor ? (selectedEmisor.series || selectedEmisor.seriesDisponibles || []) : [];
                                                        return (series || []).map(s => (
                                                            <option key={s.serie || s} value={s.serie || s}>{s.serie || s}</option>
                                                        ));
                                                    })()}
                                                    {/* then list predefined series */}
                                                    {predefinedSeries.map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Folio</Form.Label>
                                                <Form.Control size="sm" type="text" value={folio} readOnly />
                                            </Form.Group>
                                        </Col>

                                        <Col md={12} xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Forma de pago</Form.Label>
                                                <Form.Select size="sm" value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
                                                    <option value="">Seleccione forma de pago</option>
                                                    {formasPagoOptions && formasPagoOptions.length > 0 && (
                                                        formasPagoOptions.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        {/* New: Moneda, Tipo de cambio, Metodo de pago (indicativo PUE) */}
                                        <Col md={6} xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Moneda</Form.Label>
                                                <Form.Select size="sm" value={moneda} onChange={(e) => { setMoneda(e.target.value); if (e.target.value === 'MXN') setTipoCambio(1); }}>
                                                    <option value="">Seleccione moneda</option>
                                                    {Array.isArray(monedas) && monedas.length > 0 ? (
                                                        monedas.map((m, idx) => (
                                                            <option key={idx} value={m.vClaveMoneda || m.vMoneda || m.vCodigo || m.vClave}>
                                                                {m.vClaveMoneda || m.vMoneda || m.vCodigo || m.vClave}
                                                            </option>
                                                        ))
                                                    ) : (
                                                        <>
                                                            <option value="MXN">MXN</option>
                                                            <option value="USD">USD</option>
                                                            <option value="EUR">EUR</option>
                                                        </>
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Tipo de cambio</Form.Label>
                                                <Form.Control size="sm" type="number" step="0.0001" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Método de pago</Form.Label>
                                                <Form.Control size="sm" type="text" readOnly value={metodoPagoIndicativo} />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6} xs={12}>
                                            <Form.Group>
                                                <Form.Label style={{ fontSize: '0.8rem' }}>Fecha de emisión</Form.Label>
                                                <DatePicker
                                                    selected={fechaEmision ? new Date(fechaEmision) : null}
                                                    onChange={handleFechaChange}
                                                    className="form-control form-control-sm"
                                                    dateFormat="yyyy-MM-dd HH:mm:ss"
                                                    showTimeSelect
                                                    timeFormat="HH:mm"
                                                    timeIntervals={1}
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                    popperModifiers={[
                                                        {
                                                            name: "preventOverflow",
                                                            options: { boundary: "viewport" },
                                                        },
                                                        {
                                                            name: "offset",
                                                            options: { offset: [0, -290] },
                                                        },
                                                    ]}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>

                                {/* Right: productos editor + preview */}
                                <Col md={6} xs={12}>
                                    <Col md={12} xs={12}>
                                        <div className="border rounded p-2 mb-3" style={{ fontSize: '0.82rem' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <strong style={{ fontSize: '0.9rem' }}>Productos agregados</strong>
                                                <Button size="sm" variant="outline-primary" onClick={addProduct}>Agregar</Button>
                                            </div>

                                            {productos.map((p, idx) => (
                                                <div key={idx} className="mb-2 p-2 border rounded" style={{ fontSize: '0.8rem' }}>
                                                    <InputGroup className="mb-2" size="sm">

                                                        <InputGroup.Text style={{ minWidth: 120 }}>Código barras</InputGroup.Text>
                                                        <Form.Control readOnly value={p.vCodigoBarras || 'SERVICIO'} />
                                                    </InputGroup>
                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 70 }}>Código SAT</InputGroup.Text>
                                                        <Form.Control value={p.vCodigoProductoServicioSat || ''} onChange={(e) => handleProductChange(idx, 'vCodigoProductoServicioSat', e.target.value)} />
                                                    </InputGroup>
                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>Unidad SAT</InputGroup.Text>
                                                        <Form.Control value={p.vClaveUnidadSat || ''} onChange={(e) => handleProductChange(idx, 'vClaveUnidadSat', e.target.value)} />
                                                    </InputGroup>
                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 70 }}>Descripción</InputGroup.Text>
                                                        <Form.Control value={p.vNombreProducto || ''} onChange={(e) => handleProductChange(idx, 'vNombreProducto', e.target.value)} />
                                                    </InputGroup>

                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>Cantidad</InputGroup.Text>
                                                        <Form.Control type="number" value={p.dCantidad} onChange={(e) => handleProductChange(idx, 'dCantidad', Number(e.target.value))} />
                                                    </InputGroup>

                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>Precio Unit</InputGroup.Text>
                                                        <Form.Control type="number" value={p.dPrecioUnitario} onChange={(e) => handleProductChange(idx, 'dPrecioUnitario', Number(e.target.value))} />
                                                    </InputGroup>

                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>Importe</InputGroup.Text>
                                                        <Form.Control readOnly type="number" value={p.dImporte} />
                                                    </InputGroup>



                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>Tipo Factor IVA</InputGroup.Text>
                                                        <Form.Select size="sm" value={p.vTipoFactorIVA || 'Tasa'} onChange={(e) => handleProductChange(idx, 'vTipoFactorIVA', e.target.value)}>
                                                            <option value="Tasa">Tasa</option>
                                                            <option value="Cuota">Cuota</option>
                                                            <option value="Exento">Exento</option>
                                                        </Form.Select>
                                                    </InputGroup>

                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>Tasa/Cuota IVA</InputGroup.Text>
                                                        <Form.Control type="number" step="0.0001" value={p.dTasaCuotaIVA ?? 0} onChange={(e) => handleProductChange(idx, 'dTasaCuotaIVA', Number(e.target.value))} />
                                                    </InputGroup>

                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>IVA</InputGroup.Text>
                                                        <Form.Control readOnly type="number" value={p.dIVA ?? 0} />
                                                    </InputGroup>

                                                    <InputGroup className="mb-2" size="sm">
                                                        <InputGroup.Text style={{ minWidth: 120 }}>Total</InputGroup.Text>
                                                        <Form.Control readOnly type="number" value={p.dTotal ?? 0} />
                                                    </InputGroup>

                                                    <div className="d-flex justify-content-end">
                                                        <Button variant="outline-danger" size="sm" onClick={() => removeProduct(idx)}>Eliminar</Button>
                                                    </div>
                                                </div>
                                            ))}

                                        </div>

                                        <div className="border rounded p-2 bg-light" style={{ fontSize: '0.78rem' }}>
                                            <div className="border rounded p-2 bg-light" style={{ fontSize: '0.78rem' }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <strong>Datos indicativos que se enviarán</strong>
                                                    <small className="text-muted">Solo indicativo</small>
                                                </div>
                                                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto', fontSize: 10 }}>
                                                    {JSON.stringify(previewData, null, 2)}
                                                </pre>
                                            </div>
                                        </div>
                                    </Col>
                                </Col>

                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>Cancelar</Button>
                    <Button variant="primary" type="submit">Generar factura</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

FacturarModal.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    pago: PropTypes.object,
    orden: PropTypes.object,
    onSubmit: PropTypes.func
};

export default FacturarModal;
